import { Chapter, Comment as CommentType, User } from '@novelizing/types';

import { useComments } from '../../../hooks/use-comments';
import { useUserInfo } from '../../../hooks/use-user-info';
import { Comment } from './comment';

import { CommentForm } from './comment-form';
import './comments-panel.css';

interface CommentsProps {
  chapter?: Chapter;
  user?: User;
}

export const CommentsPanel = ({ chapter, user }: CommentsProps) => {
  if (!chapter) {
    return null;
  }

  const { comments, loading, error, addComment, deleteComment, updateComment } = useComments({
    chapter,
  });
  const { userInfoMap } = useUserInfo(comments as CommentType[]);

  // Helper function to organize comments into a tree structure
  const organizeCommentsIntoTree = (comments: CommentType[]) => {
    const rootComments: CommentType[] = [];
    const commentMap: Record<string, CommentType[]> = {};

    // First pass: identify all root comments and create a map for child comments
    comments.forEach((comment) => {
      // If the comment has no parent, parentId is '0', or parent is null/undefined, it's a root comment
      if (!comment.parentId || comment.parentId === '0') {
        rootComments.push(comment);
      } else {
        // Add this comment as a child of its parent
        if (!commentMap[comment.parentId]) {
          commentMap[comment.parentId] = [];
        }
        commentMap[comment.parentId].push(comment);
      }
    });

    // Sort all comment arrays by creation date (newest first)
    Object.keys(commentMap).forEach((parentId) => {
      commentMap[parentId].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    });

    // Sort root comments by creation date (newest first)
    rootComments.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    return { rootComments, commentMap };
  };

  // Organize comments into a tree structure
  const { rootComments, commentMap } = organizeCommentsIntoTree(
    comments as CommentType[],
  );

  // Handle replying to a comment
  // Handle replying to a comment
  const handleReply = async (
    content: string,
    _userId: number, // Received from Comment.tsx, but we use 'user' from props
    parentId: string, // This is the ID of the parent comment
  ): Promise<void> => {
    if (!user) {
      console.warn('User not logged in. Cannot reply.');
      // Optionally, set an error state or show a message to the user
      return;
    }
    // Assuming addComment will be updated to take parentId (string) as 3rd arg for replies
    await addComment(content, user, parentId);
  };

  return (
    <div className="comments-panel">
      <CommentForm
        user={user}
        loading={loading}
        onSubmit={async (content) => {
          if (user) {
            await addComment(content, user); // For new top-level comments, no parentId
          } else {
            console.warn('User not logged in. Cannot submit comment.');
            // Optionally, set an error state or show a message to the user
          }
        }}
      />

      {error && <div className="comments-error">{error}</div>}

      <div className="comments-list">
        {loading && comments.length === 0 ? (
          <div className="comments-loading">Loading comments...</div>
        ) : rootComments.length > 0 ? (
          rootComments.map((comment) => {
            const userIdNum = parseInt(comment.userId.toString(), 10);
            const userInfo = !isNaN(userIdNum)
              ? userInfoMap[userIdNum]
              : undefined;

            // Get child comments for this comment
            const childComments = commentMap[comment.id] || [];

            return (
              <Comment
                key={comment.id}
                comment={comment}
                userInfo={userInfo}
                childComments={childComments}
                userInfoMap={userInfoMap}
                commentMap={commentMap}
                onReply={handleReply}
                onDelete={deleteComment}
                onUpdate={updateComment}
                user={user}
                chapter={chapter}
              />
            );
          })
        ) : (
          <div className="no-comments">
            No comments yet. Be the first to comment!
          </div>
        )}
      </div>

      {loading && comments.length > 0 && (
        <div className="comments-loading-overlay">
          <div className="comments-loading">Updating comments...</div>
        </div>
      )}
    </div>
  );
};
